import { type JSX } from 'react';
import { Query as QueryApollo } from '@apollo/client/react/components';
import EkAnalys from './EkAnalys.gql';
import {
  ekanalys as IData,
  ekanalysVariables as IVariables,
  ekanalys_ekanalys_columns,
} from './_QueryTypes/ekanalys';
import Logger from '../../../../../../../Utils/Logger';
import { isDefined } from '../../../../../../../Utils/checkUtils';
import { StandardColumnType } from '../../Components/SideMenu/Components/PeriodOchKolumner/EditColumn/Components/Queries/EkonomiskAnalysStandardColumnQuery/EkonomiskAnalysStandardColumnQuery';
import { ColumnFaltkodType } from '../../Components/SideMenu/Components/PeriodOchKolumner/EditColumn/Components/EkonomiskAnalysEditColumnForm';
import { getTruePeriodType, hasAnyInterval } from '../../Utils/analysUtils';
import {
  SummationModeType,
  BudgetSummationModeType,
  defaultSummationMode,
  defaultBudgetSummationMode,
  getSummationModeOrDefault,
} from '../../Utils/SummationModeUtils';
import { DateInputType } from '../../../../../../../Utils/dateUtils';
export interface ISumData {
  LOPNR: number | null;
  Namn: string | null;
  ORDNINGS_NR_KONTODEL: number | null;
  STARTPOS: number | null;
  SLUTPOS: number | null;
  GRUPPERINGSTYP: string | null;
  DOLJ_NIVA: boolean | null;
  SIDBRYTNING: boolean | null;
  BENAMNING_FLERA_RADER: boolean | null;
  BENAMNING_BLANK: string | null;
  DELSUMMA: boolean | null;
  SKRIV_DELSUMMA: boolean | null;
  Typ: string | null;
  attributename: string | null;
}

export interface ISumCategory {
  name: string;
  code: string;
  subCategories: ISumSubCategory[];
}

export interface ISumSubCategory {
  name: string;
  code: string;
  sums: ISumData[];
}

export interface IColumn {
  name: string;
  displayLabel?: string | undefined;
  id: number;
  data: ekanalys_ekanalys_columns;
}

export interface IAnalys {
  name: string;
  loading: boolean;
  title?: string;
  category?: string;
  categoryID?: number;
  type: ReportType;
  sums: ISumData[];
  columns: IColumn[];
  defaultSettings: IAnalysSettings;
  dateInputType?: DateInputType;
  isInterval: boolean;
  anvandsFor: string[];
  sparatAnvandarUrval: string | null;
  lopsaldoDetaljrad: boolean;
  visaSummeringar: boolean;
  sparadForAnnanAnvandare?: boolean;
  expandToIndex: number | null;
  detaljanalys: boolean;
  ai_prompt: string | null;
}

export interface IQueryProps {
  id: string | null;
  standardColumns?: StandardColumnType[];
  standardColumnsQueryLoading: boolean;
  children: (data: IAnalys, loading: boolean) => JSX.Element;
}

export type ReportType =
  | 'RAPPORTDIMENSIONLOPANDE'
  | 'RAPPORTDIMENSIONRADDRIVEN'
  | 'RAPPORTDIMENSIONSAMMANSATT'
  | 'RAPPORTDIMENSIONBUDGETERINGSMODELL'
  | 'RAPPORTDIMENSIONBUDGETERINGLOPANDE'
  | 'RAPPORTDIMENSIONBUDGETERINGSUNDERLAG';

interface IAnalysSettings {
  decimals: number;
  denomination: number;
  includeEjBokfordaJournaler: boolean;
  showComparison: boolean;
  summationMode: SummationModeType;
  exkluderaPersonalKostnader: boolean;
  visaSummeringar: boolean;
  budgetSummationMode: BudgetSummationModeType;
}

const AnalysSettingsQuery = (props: IQueryProps) => {
  let analys: IAnalys = {
    loading: true,
    name: '',
    sums: [],
    columns: [],
    dateInputType: undefined,
    isInterval: false,
    type: 'RAPPORTDIMENSIONLOPANDE',
    defaultSettings: {
      decimals: 2,
      denomination: 1,
      showComparison: true,
      includeEjBokfordaJournaler: false,
      summationMode: defaultSummationMode,
      exkluderaPersonalKostnader: false,
      visaSummeringar: true,
      budgetSummationMode: defaultBudgetSummationMode,
    },
    anvandsFor: [],
    sparatAnvandarUrval: null,
    lopsaldoDetaljrad: false,
    visaSummeringar: true,
    expandToIndex: 0,
    detaljanalys: false,
    ai_prompt: null,
  };

  return (
    <QueryApollo<IData, IVariables>
      query={EkAnalys}
      variables={{ id: props.id ? props.id : '' }}
      skip={props.id === null}
    >
      {({ loading, data }) => {
        if (data) {
          const ekanalys = data.ekanalys;

          if (!loading && ekanalys && !props.standardColumnsQueryLoading) {
            const columns = ekanalys.columns
              ? ekanalys.columns.map((column, index) => {
                  if (column.INTERVALL_TYP === 0) {
                    analys.dateInputType = Math.max(
                      analys.dateInputType || 0,
                      getTruePeriodType(
                        column.FALTKOD as ColumnFaltkodType,
                        props.standardColumns
                      )
                    );
                  } else if (column.INTERVALL_TYP === 1) {
                    analys.dateInputType = DateInputType.yearMonthDay;
                  }

                  let name = column.CODE_CAPTION || 'Default Col caption';

                  if (column.BENAMNING) {
                    if (column.BENAMNING2) {
                      name = `${column.BENAMNING} (${column.BENAMNING2})`;
                    } else {
                      name = column.BENAMNING;
                    }
                  }
                  return {
                    name,
                    id: index,
                    data: column,
                  };
                })
              : [];
            if (columns.length === 0) {
              Logger.error('Ek analys columns is null');
            }
            analys.expandToIndex = ekanalys.expand_to_index ?? 0;
            analys.loading = false;
            analys.name = ekanalys.name ? ekanalys.name : '';
            analys.title = ekanalys.rubrik ? ekanalys.rubrik : '';
            analys.category = ekanalys.kategori ? ekanalys.kategori : '';
            analys.categoryID = ekanalys.kategori_id
              ? ekanalys.kategori_id
              : undefined;
            analys.type = ekanalys.typ ? ekanalys.typ : analys.type;
            analys.sparatAnvandarUrval = ekanalys.anv_urval;
            analys.defaultSettings = {
              decimals:
                ekanalys.standard_belopp_antal_decimaler != null
                  ? ekanalys.standard_belopp_antal_decimaler
                  : analys.defaultSettings.decimals,
              denomination: ekanalys.standard_belopp_bas
                ? Number.parseInt(ekanalys.standard_belopp_bas, 10)
                : 1,
              includeEjBokfordaJournaler: !ekanalys.endast_bokfort,
              showComparison:
                ekanalys.standard_visa_jamforelse == null
                  ? analys.defaultSettings.showComparison
                  : Boolean(ekanalys.standard_visa_jamforelse),
              summationMode: getSummationModeOrDefault(
                ekanalys.standard_summerings_mode
              ),
              exkluderaPersonalKostnader:
                ekanalys.standard_exkludera_personalkostnader ?? false,
              visaSummeringar: ekanalys.visa_summering ?? true,
              budgetSummationMode: defaultBudgetSummationMode,
            };
            analys.anvandsFor = ekanalys.anvands_for
              ? ekanalys.anvands_for.filter(isDefined)
              : [];
            analys.lopsaldoDetaljrad = ekanalys.lopsaldodetaljrad ?? false;
            analys.visaSummeringar = ekanalys.visa_summering ?? true;
            analys.sparadForAnnanAnvandare =
              ekanalys.sparadForAnnanAnvandare ?? false;
            analys.detaljanalys = ekanalys.detaljanalys ?? false;
            analys.ai_prompt = ekanalys.ai_prompt ?? null;

            if (ekanalys.sums) {
              analys.sums = ekanalys.sums.map((x: ISumData) => ({
                LOPNR: x.LOPNR,
                Namn: x.Namn,
                ORDNINGS_NR_KONTODEL: x.ORDNINGS_NR_KONTODEL,
                STARTPOS: x.STARTPOS,
                SLUTPOS: x.SLUTPOS,
                GRUPPERINGSTYP: x.GRUPPERINGSTYP,
                DOLJ_NIVA: x.DOLJ_NIVA,
                SIDBRYTNING: x.SIDBRYTNING,
                BENAMNING_FLERA_RADER: x.BENAMNING_FLERA_RADER,
                BENAMNING_BLANK: x.BENAMNING_BLANK,
                DELSUMMA: x.DELSUMMA,
                SKRIV_DELSUMMA: x.SKRIV_DELSUMMA,
                Typ: x.Typ,
                attributename: x.attributename,
              }));
            } else {
              // Logger.error('Ek analys sums is null');
              analys.sums = [];
            }

            analys.columns = columns;
            analys.isInterval = hasAnyInterval(
              analys.columns,
              props.standardColumns
            );
          }
        }

        return props.children(analys, loading);
      }}
    </QueryApollo>
  );
};

export default AnalysSettingsQuery;
